import React, { useState, useEffect } from 'react';
import { BackupPolicy, getBackupPolicy, upsertBackupPolicy } from '../api';
import { Callout, Checkbox, Input, Section, Select } from './primitives';
import { Field, LoadingRows } from './ui';

interface BackupPolicyCardProps {
  projectId: string;
  onPolicyUpdated?: () => void;
}

export const BackupPolicyCard: React.FC<BackupPolicyCardProps> = ({ projectId, onPolicyUpdated }) => {
  const [policy, setPolicy] = useState<BackupPolicy | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [saving, setSaving] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<boolean>(false);

  useEffect(() => {
    loadPolicy();
  }, [projectId]);

  const loadPolicy = async () => {
    setLoading(true);
    setError(null);
    try {
      const resp = await getBackupPolicy(projectId);
      setPolicy(resp.backup_policy);
    } catch (err: any) {
      setError(err.message || 'The backup policy could not be loaded.');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!policy) return;

    setSaving(true);
    setError(null);
    setSuccess(false);

    try {
      const resp = await upsertBackupPolicy(projectId, {
        frequency: policy.frequency,
        retention_days: Number(policy.retention_days),
        retain_daily_count: Number(policy.retain_daily_count),
        retain_weekly_count: Number(policy.retain_weekly_count),
        pre_deploy_checkpoint_enabled: policy.pre_deploy_checkpoint_enabled,
      });
      setPolicy(resp.backup_policy);
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
      if (onPolicyUpdated) onPolicyUpdated();
    } catch (err: any) {
      setError(err.message || 'The backup policy could not be saved.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Section title="Backup schedule and retention" testId="backup-policy">
        <LoadingRows label="Loading the backup policy…" rows={3} rowHeight="52px" />
      </Section>
    );
  }

  return (
    <Section
      title="Backup schedule and retention"
      testId="backup-policy"
    >
      {error ? <Callout tone="danger" title={error} /> : null}

      {success ? <Callout tone="success" title="Backup policy saved" /> : null}

      {policy ? (
        <form className="stack" onSubmit={handleSubmit}>
          <div className="form-grid form-grid--2">
            <Field label="Frequency">
              <Select value={policy.frequency} onChange={(e) => setPolicy({ ...policy, frequency: e.target.value })}>
                <option value="daily">Daily</option>
                <option value="weekly">Weekly</option>
                <option value="manual_only">Manual only</option>
              </Select>
            </Field>
            <Field label="Retention (days)">
              <Input
                type="number"
                min="1"
                max="365"
                value={policy.retention_days}
                onChange={(e) => setPolicy({ ...policy, retention_days: parseInt(e.target.value, 10) || 14 })}
              />
            </Field>
            <Field label="Daily backups to keep">
              <Input
                type="number"
                min="1"
                max="90"
                value={policy.retain_daily_count}
                onChange={(e) => setPolicy({ ...policy, retain_daily_count: parseInt(e.target.value, 10) || 7 })}
              />
            </Field>
            <Field label="Weekly backups to keep">
              <Input
                type="number"
                min="0"
                max="52"
                value={policy.retain_weekly_count}
                onChange={(e) => setPolicy({ ...policy, retain_weekly_count: parseInt(e.target.value, 10) || 4 })}
              />
            </Field>
          </div>

          <Checkbox
            checked={policy.pre_deploy_checkpoint_enabled}
            onChange={(e) => setPolicy({ ...policy, pre_deploy_checkpoint_enabled: e.target.checked })}
            label={<>Take a database checkpoint before a deployment runs <code>setup:upgrade</code> schema changes.</>}
          />

          <button type="submit" className="button" disabled={saving}>
            {saving ? 'Saving' : 'Save backup policy'}
          </button>
        </form>
      ) : null}
    </Section>
  );
};
